import React, { useRef, FC, useEffect, RefObject } from 'react';
import '@inter-ikea-kompis/component-drag-and-drop';
import { Orientation, DeviceType, WriteDirection, Platform } from 'sofa-reborn-react';
import {
	DivBuildArea,
	DivSelector,
	BuildAreaWrapper,
	PickersStyle,
	CoverOnlyDisclaimerStyle,
} from '../BuildArea/BuildAreaStyle';
import CoverPickerButtonContainer from '../../containers/CoverPickerButtonContainer';
import ArmrestPickerButtonContainer from '../../containers/ArmrestPickerButtonContainer';
import ModuleSwiperContainer from '../../containers/ModuleSwiperContainer';
import Scene, { SceneContainer } from '../../containers/SceneContainer';
import HeaderContainer from '../../containers/HeaderContainer';
import FooterContainer from '../../containers/FooterContainer';
import ActionButtonsContainer from '../../containers/ActionButtonsContainer';
import DeleteOverlayContainer from '../../containers/DeleteOverlayContainer';
import BannerContainer from '../../containers/BannerContainer';
import { DragAndDropAreaEventEnum } from '@inter-ikea-kompis/component-drag-and-drop';
import CoverDisclaimer from '../CoverPicker/CoverDisclaimer';
import { localeService } from '../../services';
import { SkapaTheme } from '@inter-ikea-kompis/themes/lib';

type Props = {
	orientation: Orientation;
	deviceType: DeviceType;
	platform: Platform;
	writeDirection: WriteDirection;
	actionButtonsVisible?: boolean;
	disableInput?: boolean;
	dragSceneItemId: string | null;
	overSceneItemId: string | null;
	pointerOverDragArea: boolean;
	footerHeight: number;
	hasArmrestPicker: boolean;
	coverOnly: boolean | undefined;
	right: string;
	showBanner: boolean;
	bannerHasBeenShown: boolean;
	showArea: boolean;
	showDeleteOverlay: boolean;
	onCloseAllPopovers?: () => void;
	onCloseAllBanners?: () => void;
};

const BuildArea: FC<Props> = (props) => {
	const sceneRef = useRef<SceneContainer>();
	const dragAndDropRef = useRef<DragAndDrop>();

	const onPointerDown = (e: CustomEvent): void => {
		if (sceneRef && sceneRef.current) {
			sceneRef.current.onPointerDown(e);
		}
	};

	const onPointerUp = (e: CustomEvent): void => {
		if (sceneRef && sceneRef.current) {
			sceneRef.current.onPointerUp(e);
		}
	};

	const onPointerMove = (e: CustomEvent): void => {
		if (sceneRef && sceneRef.current) {
			sceneRef.current.onPointerMove(e);
		}
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (
			sceneRef &&
			sceneRef.current &&
			(e.key === 'Enter' || e.key === ' ' || e.key === 'Escape')
		) {
			if (e.key === 'Escape') {
				props.onCloseAllBanners && props.onCloseAllBanners();
				props.onCloseAllPopovers && props.onCloseAllPopovers();
			} else {
				const element = e.target as HTMLElement;
				const hasDisabledAttribute = element.getAttribute('disabled') !== null;

				if (element.id === 'module-swiper-item' && !hasDisabledAttribute) {
					sceneRef.current.onKeyDown();
				}
			}
		}
	};

	useEffect(() => {
		if (dragAndDropRef.current) {
			const dragAndDrop = dragAndDropRef.current;
			dragAndDrop.addEventListener(DragAndDropAreaEventEnum.pointerUp, onPointerUp);
			dragAndDrop.addEventListener(DragAndDropAreaEventEnum.pointerDown, onPointerDown);
			dragAndDrop.addEventListener(DragAndDropAreaEventEnum.pointerMove, onPointerMove);
			document.addEventListener('badDesktopOSMouseUp', onPointerUp);
		}
		document.addEventListener('keydown', onKeyDown);

		return (): void => {
			if (dragAndDropRef.current) {
				const dragAndDrop = dragAndDropRef.current;
				dragAndDrop.removeEventListener(DragAndDropAreaEventEnum.pointerUp, onPointerUp);
				dragAndDrop.removeEventListener(DragAndDropAreaEventEnum.pointerDown, onPointerDown);
				dragAndDrop.removeEventListener(DragAndDropAreaEventEnum.pointerMove, onPointerMove);
				document.removeEventListener('badDesktopOSMouseUp', onPointerUp);
			}
			document.removeEventListener('keydown', onKeyDown);
		};
	}, []);

	return (
		<kompis-drag-and-drop-area
			ref={dragAndDropRef}
			disableInput={props.disableInput ? true : null}
			areaId={'scene'}
			style={{
				borderTop: props.platform !== 'kiosk' ? '1px solid ' + SkapaTheme.color.border.default : '',
				borderBottom:
					props.platform !== 'kiosk' ? '1px solid ' + SkapaTheme.color.border.default : '',
				boxSizing: props.platform !== 'kiosk' ? 'border-box' : '',
				width: '100%',
			}}
		>
			{props.showArea && props.showBanner && !props.bannerHasBeenShown ? <BannerContainer /> : null}
			<BuildAreaWrapper
				orientation={props.orientation}
				writeDirection={props.writeDirection}
				platform={props.platform}
				deviceType={props.deviceType}
				dragSceneItemId={props.dragSceneItemId as string}
				overSceneItemId={props.overSceneItemId as string}
				pointerOverDragArea={props.pointerOverDragArea}
				footerHeight={props.footerHeight}
			>
				<DivBuildArea
					deviceType={props.deviceType}
					orientation={props.orientation}
					platform={props.platform}
					writeDirection={props.writeDirection}
				>
					<HeaderContainer onSummaryPage={false} />
					{props.coverOnly && (props.deviceType === 'mobile' || props.deviceType === 'tablet') && (
						<div style={{ width: '96%', maxWidth: '370px' }}>
							<CoverDisclaimer
								writeDirection={props.writeDirection}
								title={''}
								text={localeService.getTranslation('coverOnlyDisclaimerText')}
							/>
						</div>
					)}
					<Scene ref={sceneRef as RefObject<SceneContainer>} />
					{props.coverOnly && props.deviceType !== 'mobile' && props.deviceType !== 'tablet' && (
						<CoverOnlyDisclaimerStyle platform={props.platform} right={props.right}>
							<CoverDisclaimer
								writeDirection={props.writeDirection}
								title={localeService.getTranslation('coverOnlyDisclaimerTitle')}
								text={localeService.getTranslation('coverOnlyDisclaimerText')}
							/>
						</CoverOnlyDisclaimerStyle>
					)}
				</DivBuildArea>
				{props.showArea && (
					<DivSelector
						id="selector"
						deviceType={props.deviceType}
						orientation={props.orientation}
						platform={props.platform}
						writeDirection={props.writeDirection}
					>
						{props.deviceType === 'mobile' && props.actionButtonsVisible ? null : (
							<>
								<PickersStyle
									deviceType={props.deviceType}
									orientation={props.orientation}
									writeDirection={props.writeDirection}
									hasArmrestPicker={props.hasArmrestPicker}
								>
									<CoverPickerButtonContainer />
									<ArmrestPickerButtonContainer />
								</PickersStyle>
								<ModuleSwiperContainer />
							</>
						)}
						{props.deviceType === 'mobile' && props.actionButtonsVisible ? (
							<ActionButtonsContainer />
						) : null}
						{props.showDeleteOverlay ? <DeleteOverlayContainer /> : null}
					</DivSelector>
				)}
				{props.showArea && props.orientation !== 'portrait' ? <FooterContainer /> : null}
			</BuildAreaWrapper>
		</kompis-drag-and-drop-area>
	);
};

export default BuildArea;
